import { useTranslation } from 'react-i18next';

import { Avatar, Flex, SkeletonText, Text } from '@bonusly/design-system';

import type { BizyPersonPickerNode } from '../../../../lib/bizy/types/nodes';
import { useBizyChatActions } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { useBizyUserResolver } from '../context/user-resolver-context';
import type { BizyNodeProps } from './node-props';

type Person = BizyPersonPickerNode['payload']['people'][number];

/**
 * Renders the candidate people for an ambiguous name lookup ("Thomas") as
 * one-tap cards under a "People" heading. Clicking a card sends that person's
 * `message` verbatim (via the follow-up send path) so the next turn resolves to
 * exactly one person.
 *
 * The node payload is externalUserId-only — name / avatar / subtitle are
 * resolved through the injected user resolver, mirroring meeting_people_card.
 * Read-only views (super-admin history, shared transcripts) still render the
 * cards as a static preview so reviewers see what was offered, but they're not
 * selectable — tapping never sends on the viewer's behalf. Interactive cards
 * disable while a previous turn is still processing.
 */
export function PersonPickerNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyPersonPickerNode;
	const { t } = useTranslation('admin_bizy');
	const { track } = useBizyAnalytics();
	const { fontSize = 'sm', isReadOnly, followUpsDisabled, onSelectFollowUp } = useBizyChatActions();

	const items = payload.people.filter((person) => person.message.trim().length > 0);
	if (items.length === 0) return null;

	// Read-only views (super-admin history, shared transcripts) still render the
	// candidate cards so reviewers see exactly what the user was offered — but
	// they're inert: tapping never sends on the viewer's behalf, and without a
	// follow-up handler there's nowhere to send anyway.
	const isSelectable = !isReadOnly && !!onSelectFollowUp;

	const handleSelect = (person: Person, position: number) => {
		if (!isSelectable || followUpsDisabled) return;
		track(BizyAnalyticsEvent.PersonPickerSelected, { position, path: window.location.pathname });
		onSelectFollowUp?.(person.message);
	};

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-person-picker">
			<Text fontSize="md" fontWeight="semibold" color="text.default" mb={0.5}>
				{t('personPicker.heading', 'People')}
			</Text>
			{items.map((person, idx) => (
				<PersonCard
					key={idx}
					person={person}
					fontSize={fontSize}
					isSelectable={isSelectable}
					isDisabled={followUpsDisabled}
					onSelect={() => handleSelect(person, idx)}
				/>
			))}
		</Flex>
	);
}

function PersonCard({
	person,
	fontSize,
	isSelectable,
	isDisabled,
	onSelect,
}: {
	person: Person;
	fontSize: 'sm' | 'md';
	isSelectable: boolean;
	isDisabled: boolean;
	onSelect: () => void;
}) {
	const resolveUser = useBizyUserResolver();
	const resolved = resolveUser(person.externalUserId);
	const displayName = resolved.displayName ?? '';
	const subtitle = resolved.subtitle;
	// Only the name needs the identity fetch; show a skeleton until it resolves
	// so we don't flash the email-bearing `message` string.
	const isLoadingThisRow = resolved.isLoading && !resolved.displayName && !!person.externalUserId;

	// Interactive cards are tappable buttons that dim while a turn is
	// processing. Read-only cards render as a plain, full-opacity row with no
	// button semantics or hover affordance — visible, but not selectable.
	const interactiveProps = isSelectable
		? {
				as: 'button' as const,
				type: 'button' as const,
				disabled: isDisabled,
				onClick: onSelect,
				opacity: isDisabled ? 0.5 : 1,
				cursor: isDisabled ? 'not-allowed' : 'pointer',
				...(!isDisabled && { _hover: { bg: 'bg.subtle', borderColor: 'border.default' } }),
			}
		: {};

	return (
		<Flex
			{...interactiveProps}
			aria-label={displayName || person.message}
			align="center"
			gap={3}
			textAlign="left"
			borderWidth="1px"
			borderColor="border.soft"
			borderRadius={12}
			bg="bg.surface"
			px={3}
			py={2.5}
			transition="background 0.15s ease, border-color 0.15s ease"
		>
			<Avatar name={displayName} src={resolved.avatarUrl} size="sm" flexShrink={0} />
			<Flex direction="column" flex="1 1 auto" minW={0} gap={0.5}>
				{isLoadingThisRow ? (
					<SkeletonText noOfLines={1} skeletonHeight="3" width="60%" />
				) : (
					<Text fontSize={fontSize} fontWeight="semibold" color="text.default" noOfLines={1}>
						{displayName || person.message}
					</Text>
				)}
				{subtitle && (
					<Text fontSize="xs" color="text.soft" noOfLines={1} lineHeight="short">
						{subtitle}
					</Text>
				)}
			</Flex>
		</Flex>
	);
}
